import {
  Box as Form,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
} from '@chakra-ui/react';
import { Container } from 'components/Layout/Container';
import { useAuth } from 'context/AuthUserContext';
import Link from 'next/link';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTheme } from 'styled-components';

type Inputs = {
  email: string;
  password: string;
  failedAttempt?: string;
};

export const SignInForm = () => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<Inputs>();
  const { signInWithEmailAndPassword } = useAuth();
  const { colors } = useTheme();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const { email, password } = data;

    try {
      await signInWithEmailAndPassword(email, password);
    } catch (error: any) {
      if (
        error?.code === 'auth/wrong-password' ||
        error?.code === 'auth/user-not-found'
      ) {
        setError('failedAttempt', {
          message: 'Email or password is incorrect.',
        });
      }

      if (error?.code === 'auth/too-many-requests') {
        setError('failedAttempt', {
          message:
            'Access to this account has been temporarily disabled due to many failed login attempts. Please try again later.',
        });
      }
      console.log({ error });
    }
  };

  return (
    <Container display="flex" justifyContent="center" p="75px 0 150px 0">
      <Form
        maxW="500px"
        w="100%"
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        borderRadius="4px"
        p="35px 50px"
        bg={colors.white}
      >
        <FormControl
          isInvalid={!!errors.email || !!errors.failedAttempt}
          mb="12px"
        >
          <FormLabel htmlFor="email">Email</FormLabel>
          <Input
            id="email"
            placeholder="name@example.com"
            type="email"
            {...register('email', {
              required: 'Name is required',
              onChange: () => clearErrors('failedAttempt'),
            })}
          />
          <FormErrorMessage>
            {errors.email && errors.email.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.password || !!errors.failedAttempt}>
          <FormLabel htmlFor="password">Password</FormLabel>
          <Input
            id="password"
            type="password"
            placeholder="Minimum 8 Characters"
            {...register('password', {
              required: 'Password is required',
              onChange: () => clearErrors('failedAttempt'),
              minLength: { value: 8, message: 'Minimum length should be 8' },
            })}
          />
          <FormErrorMessage>
            {errors.password && errors.password.message}
          </FormErrorMessage>
          <FormErrorMessage>
            {errors.failedAttempt && errors.failedAttempt.message}
          </FormErrorMessage>
        </FormControl>
        <Link href="/reset-password" passHref>
          <Text
            display="block"
            mt="15px"
            fontWeight={500}
            as="a"
            color="#3ba1df"
            textDecoration="underline"
          >
            Forgot password?
          </Text>
        </Link>
        <Button
          mt={3}
          isLoading={isSubmitting}
          type="submit"
          variant="primary"
          w="100%"
        >
          Sign In
        </Button>
        <Text mt="30px" fontWeight={500} textAlign="center">
          Don&apos;t have an account?{' '}
          <Link href="/sign-up" passHref>
            <Text as="a" color="#3ba1df" textDecoration="underline">
              Sign Up
            </Text>
          </Link>
        </Text>
      </Form>
    </Container>
  );
};
